<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import Button from 'dashboard/components-next/button/Button.vue';
import MapPicker from './MapPicker.vue';

const props = defineProps({
  type: { type: String, required: true },
  entry: { type: Object, default: null },
});

const emit = defineEmits(['saved', 'cancel']);

const TYPE_I18N_KEYS = {
  order_types: 'CEA_LOOKUPS.TYPE_LABELS.ORDER_TYPES',
  order_motives: 'CEA_LOOKUPS.TYPE_LABELS.ORDER_MOTIVES',
  repair_codes: 'CEA_LOOKUPS.TYPE_LABELS.REPAIR_CODES',
  subcategory_tipos: 'CEA_LOOKUPS.TYPE_LABELS.SUBCATEGORY_TIPOS',
  cea_locations: 'CEA_LOOKUPS.TYPE_LABELS.OFICINAS',
};

const { t } = useI18n();

const isLocation = computed(() => props.type === 'cea_locations');

const form = ref({
  code: '',
  description: '',
  active: true,
  order_class: 'work_order',
});

const locationForm = ref({
  slug: '',
  name: '',
  tipo: 'oficina',
  address_street: '',
  colonia: '',
  municipio: 'Queretaro',
  codigo_postal: '',
  latitude: '',
  longitude: '',
  telefono: '',
  horario: '{}',
  servicios: '',
  is_active: true,
  notas: '',
});

const showOrderClass = computed(() => props.type === 'order_types');
const isRepairCode = computed(() => props.type === 'repair_codes');

const mapPickerRef = ref(null);
const geocoding = ref(false);

const searchOnMap = async () => {
  const parts = [
    locationForm.value.address_street,
    locationForm.value.colonia,
    locationForm.value.municipio,
    locationForm.value.codigo_postal,
  ].filter(Boolean);

  if (!parts.length || !mapPickerRef.value) return;

  geocoding.value = true;
  try {
    const found = await mapPickerRef.value.geocode(parts.join(', '));
    if (!found) useAlert(t('CEA_LOOKUPS.FORM.GEOCODE_NO_RESULTS'));
  } catch {
    useAlert(t('CEA_LOOKUPS.FORM.GEOCODE_ERROR'));
  } finally {
    geocoding.value = false;
  }
};

const saving = ref(false);
const isEditing = computed(() => !!props.entry?.id);
const typeLabel = computed(() => t(TYPE_I18N_KEYS[props.type] || props.type));
const modalTitle = computed(() =>
  isEditing.value
    ? t('CEA_LOOKUPS.FORM.EDIT_TITLE', { type: typeLabel.value })
    : t('CEA_LOOKUPS.FORM.NEW_TITLE', { type: typeLabel.value })
);

// Subcategory mapping state (repair codes only)
const allSubcategories = ref([]);
const selectedSubcategoryIds = ref([]);
const loadingSubcategories = ref(false);

const filteredSubcategories = computed(() =>
  allSubcategories.value.filter(
    sc => sc.code?.startsWith('REP-') || sc.code?.startsWith('SRV-')
  )
);

const fetchSubcategories = async () => {
  if (!isRepairCode.value || !isEditing.value) return;

  loadingSubcategories.value = true;
  try {
    const [categoriesRes, recordRes] = await Promise.all([
      TicketCategoriesAPI.get(),
      CeaLookupsAPI.show(props.type, props.entry.id),
    ]);

    const categories = categoriesRes.data || [];
    allSubcategories.value = categories.flatMap(
      cat => cat.ticket_subcategories || []
    );
    selectedSubcategoryIds.value = recordRes.data.subcategory_ids || [];
  } catch {
    // Subcategory loading failure is non-blocking
  } finally {
    loadingSubcategories.value = false;
  }
};

const toggleSubcategory = id => {
  const idx = selectedSubcategoryIds.value.indexOf(id);
  if (idx >= 0) {
    selectedSubcategoryIds.value.splice(idx, 1);
  } else {
    selectedSubcategoryIds.value.push(id);
  }
};

onMounted(() => {
  if (isLocation.value && props.entry) {
    locationForm.value = {
      slug: props.entry.slug || '',
      name: props.entry.name || '',
      tipo: props.entry.tipo || 'oficina',
      address_street: props.entry.address_street || '',
      colonia: props.entry.colonia || '',
      municipio: props.entry.municipio || 'Queretaro',
      codigo_postal: props.entry.codigo_postal || '',
      latitude: props.entry.latitude || '',
      longitude: props.entry.longitude || '',
      telefono: props.entry.telefono || '',
      horario: JSON.stringify(props.entry.horario || {}),
      servicios: Array.isArray(props.entry.servicios)
        ? props.entry.servicios.join(', ')
        : '',
      is_active: props.entry.is_active !== false,
      notas: props.entry.notas || '',
    };
  } else if (props.entry) {
    form.value = {
      code: props.entry.code || '',
      description: props.entry.description || '',
      active: props.entry.active !== false,
      order_class: props.entry.order_class || 'work_order',
    };
  }
  fetchSubcategories();
});

const buildLocationPayload = () => {
  let horario = {};
  try {
    horario = JSON.parse(locationForm.value.horario);
  } catch {
    // keep empty object
  }

  const servicios = locationForm.value.servicios
    ? locationForm.value.servicios
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];

  return {
    slug: locationForm.value.slug,
    name: locationForm.value.name,
    tipo: locationForm.value.tipo,
    address_street: locationForm.value.address_street,
    colonia: locationForm.value.colonia,
    municipio: locationForm.value.municipio,
    codigo_postal: locationForm.value.codigo_postal,
    latitude: parseFloat(locationForm.value.latitude) || 0,
    longitude: parseFloat(locationForm.value.longitude) || 0,
    telefono: locationForm.value.telefono,
    horario,
    servicios,
    is_active: locationForm.value.is_active,
    notas: locationForm.value.notas,
  };
};

const handleSubmit = async () => {
  saving.value = true;

  const payload = isLocation.value
    ? buildLocationPayload()
    : (() => {
        const p = { ...form.value };
        if (!showOrderClass.value) delete p.order_class;
        return p;
      })();

  try {
    if (isEditing.value) {
      const subcatIds = isRepairCode.value
        ? selectedSubcategoryIds.value
        : null;
      await CeaLookupsAPI.update(
        props.type,
        props.entry.id,
        payload,
        subcatIds
      );
      useAlert(t('CEA_LOOKUPS.UPDATE_SUCCESS'));
    } else {
      await CeaLookupsAPI.create(props.type, payload);
      useAlert(t('CEA_LOOKUPS.CREATE_SUCCESS'));
    }
    emit('saved');
  } catch (error) {
    const errors = error.response?.data?.errors || [
      t('CEA_LOOKUPS.SAVE_ERROR'),
    ];
    useAlert(Array.isArray(errors) ? errors.join(', ') : errors);
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="flex flex-col w-full"
    :class="isLocation ? 'max-w-2xl' : 'max-w-lg'"
  >
    <woot-modal-header :header-title="modalTitle" />

    <!-- Location form (cea_locations) -->
    <form
      v-if="isLocation"
      class="flex flex-col gap-4 p-6"
      @submit.prevent="handleSubmit"
    >
      <div class="grid grid-cols-2 gap-4">
        <!-- Name -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.NAME') }} *
          </label>
          <input
            v-model="locationForm.name"
            type="text"
            required
            maxlength="200"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.NAME_PLACEHOLDER')"
          />
        </div>

        <!-- Slug -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.SLUG') }} *
          </label>
          <input
            v-model="locationForm.slug"
            type="text"
            required
            maxlength="100"
            class="w-full px-3 py-2 font-mono rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.SLUG_PLACEHOLDER')"
          />
        </div>

        <!-- Tipo -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.TIPO') }} *
          </label>
          <select
            v-model="locationForm.tipo"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
          >
            <option value="oficina">
              {{ $t('CEA_LOOKUPS.OFICINAS.TIPO_OFICINA') }}
            </option>
            <option value="cajero">
              {{ $t('CEA_LOOKUPS.OFICINAS.TIPO_CAJERO') }}
            </option>
          </select>
        </div>

        <!-- Phone -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.PHONE') }}
          </label>
          <input
            v-model="locationForm.telefono"
            type="text"
            maxlength="20"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.PHONE_PLACEHOLDER')"
          />
        </div>

        <!-- Address -->
        <div class="flex flex-col gap-1 col-span-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.ADDRESS_STREET') }} *
          </label>
          <input
            v-model="locationForm.address_street"
            type="text"
            required
            maxlength="255"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.ADDRESS_PLACEHOLDER')"
          />
        </div>

        <!-- Colonia -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.COLONIA') }} *
          </label>
          <input
            v-model="locationForm.colonia"
            type="text"
            required
            maxlength="100"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.COLONIA_PLACEHOLDER')"
          />
        </div>

        <!-- Municipio -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.MUNICIPIO') }}
          </label>
          <input
            v-model="locationForm.municipio"
            type="text"
            maxlength="100"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.MUNICIPIO_PLACEHOLDER')"
          />
        </div>

        <!-- Postal Code -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.POSTAL_CODE') }}
          </label>
          <input
            v-model="locationForm.codigo_postal"
            type="text"
            maxlength="10"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="$t('CEA_LOOKUPS.FORM.POSTAL_CODE_PLACEHOLDER')"
          />
        </div>

        <!-- Map Picker -->
        <div class="flex flex-col gap-2 col-span-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.FORM.LOCATION_MAP') }}
          </label>
          <Button
            type="button"
            slate
            faded
            xs
            :is-loading="geocoding"
            :label="$t('CEA_LOOKUPS.FORM.SEARCH_MAP')"
            icon="search"
            @click="searchOnMap"
          />
          <MapPicker
            ref="mapPickerRef"
            v-model:latitude="locationForm.latitude"
            v-model:longitude="locationForm.longitude"
          />
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">
                {{ $t('CEA_LOOKUPS.FORM.LATITUDE') }} *
              </label>
              <input
                v-model="locationForm.latitude"
                type="number"
                step="0.0000001"
                required
                class="w-full px-3 py-2 font-mono text-sm rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">
                {{ $t('CEA_LOOKUPS.FORM.LONGITUDE') }} *
              </label>
              <input
                v-model="locationForm.longitude"
                type="number"
                step="0.0000001"
                required
                class="w-full px-3 py-2 font-mono text-sm rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Horario -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.HOURS') }}
        </label>
        <textarea
          v-model="locationForm.horario"
          rows="3"
          class="w-full px-3 py-2 font-mono text-sm rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        />
      </div>

      <!-- Servicios -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.SERVICES') }}
        </label>
        <input
          v-model="locationForm.servicios"
          type="text"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        />
      </div>

      <!-- Notas -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.NOTES') }}
        </label>
        <textarea
          v-model="locationForm.notas"
          rows="2"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        />
      </div>

      <!-- Active -->
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          v-model="locationForm.is_active"
          type="checkbox"
          class="rounded"
        />
        <span class="text-sm text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.ACTIVE') }}
        </span>
      </label>

      <!-- Actions -->
      <div class="flex justify-end gap-2 pt-4">
        <Button
          slate
          faded
          :label="$t('CEA_LOOKUPS.FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          :is-loading="saving"
          :label="
            isEditing
              ? $t('CEA_LOOKUPS.FORM.UPDATE')
              : $t('CEA_LOOKUPS.FORM.CREATE')
          "
        />
      </div>
    </form>

    <!-- Standard lookup form -->
    <form v-else class="flex flex-col gap-4 p-6" @submit.prevent="handleSubmit">
      <!-- Code -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.CODE') }} *
        </label>
        <input
          v-model="form.code"
          type="text"
          required
          maxlength="10"
          class="w-full px-3 py-2 font-mono uppercase rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
          :placeholder="$t('CEA_LOOKUPS.FORM.CODE_PLACEHOLDER')"
        />
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.DESCRIPTION') }} *
        </label>
        <input
          v-model="form.description"
          type="text"
          required
          maxlength="255"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        />
      </div>

      <!-- Active -->
      <label class="flex items-center gap-2 cursor-pointer">
        <input v-model="form.active" type="checkbox" class="rounded" />
        <span class="text-sm text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.ACTIVE') }}
        </span>
      </label>

      <!-- Order Class (only for order_types) -->
      <div v-if="showOrderClass" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Clase de Orden
        </label>
        <select
          v-model="form.order_class"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        >
          <option value="work_order">Orden de Trabajo</option>
          <option value="service_order">Orden de Servicio</option>
        </select>
      </div>

      <!-- Subcategory Mapping (only for repair_codes in edit mode) -->
      <div v-if="isRepairCode && isEditing" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.FORM.SUBCATEGORIES_LABEL') }}
        </label>
        <p class="text-xs text-n-slate-11">
          {{ $t('CEA_LOOKUPS.FORM.SUBCATEGORIES_HINT') }}
        </p>
        <div v-if="loadingSubcategories" class="text-sm text-n-slate-11 py-2">
          {{ $t('CEA_LOOKUPS.FORM.LOADING_SUBCATEGORIES') }}
        </div>
        <div
          v-else
          class="max-h-48 overflow-y-auto rounded-lg border border-n-weak p-2 flex flex-col gap-1"
        >
          <label
            v-for="sc in filteredSubcategories"
            :key="sc.id"
            class="flex items-center gap-2 px-2 py-1 rounded hover:bg-n-alpha-1 cursor-pointer"
          >
            <input
              type="checkbox"
              class="rounded"
              :checked="selectedSubcategoryIds.includes(sc.id)"
              @change="toggleSubcategory(sc.id)"
            />
            <span class="text-sm text-n-slate-12">
              {{ sc.code }} - {{ sc.name }}
            </span>
          </label>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 pt-4">
        <Button
          slate
          faded
          :label="$t('CEA_LOOKUPS.FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          :is-loading="saving"
          :label="
            isEditing
              ? $t('CEA_LOOKUPS.FORM.UPDATE')
              : $t('CEA_LOOKUPS.FORM.CREATE')
          "
        />
      </div>
    </form>
  </div>
</template>
